import React, { useEffect, useMemo, useState } from "react";
import { Container, Row as BsRow } from "react-bootstrap";
import { Flex, Divider, Radio, Badge } from "antd";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon, FilterIcon, HajiIcon } from "@hugeicons/core-free-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AntInput } from "../../../CommonElements/AntInput";
import BtnAsLink from "../../../Component/BtnAsLink";
import { useGetCheckinArrivalsQuery } from "../../../services/arrivals";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import ArrivalCardSkeleton from "../Reception/ArrivalCardSkeleton";
import { ArrivalCard } from "../Reception/ArrivalCard";
import { useArrival } from "../Reception/hooks/useArrival";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { GenericFilterModal } from "@/CommonElements/TableC";
import { AntPagination } from "@/Component/AntPagination/AntPagination";

const CampaignAccommodationList = ({ port }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "delegation";
  const isNusuk = source === "nusuk";
  const {
    filters,
    isFilterOpen,
    setIsFilterOpen,
    handleFilterApply,
    handleFilterReset,
  } = useFilter();

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const [dateQuery, setDateQuery] = useState(
    searchParams.get("date") || "today"
  );
  const {
    data: arrivalsRes,
    isLoading,
    isFetching,
  } = useGetCheckinArrivalsQuery({
    port,
    search: debouncedValue,
    page: currentPage,
    date: dateQuery,
    filters,
  });

  const arrivals = useMemo(
    () => arrivalsRes?.data?.data || [],
    [arrivalsRes?.data?.data]
  );
  const { labelValues, filterFields, title } = useArrival(
    arrivalsRes?.data?.date
  );
  const loading = isLoading || isFetching;

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Update selectedArrival when arrivals change
  useEffect(() => {
    if (selectedArrival && arrivals.length > 0) {
      const updated = arrivals.find((a) => a.id === selectedArrival.id);
      if (updated) {
        setSelectedArrival(updated);
      }
    }
  }, [arrivals, selectedArrival]);

  // Status mapping similar to bus feature based on bus_cards
  const getArrivalStatus = (busCards) => {
    if (!busCards || busCards.length === 0) {
      return {
        status: "no_buses",
        color: "danger",
        text: t("NO_BUSES_FOUND"),
        icon: Search01Icon,
        arrivedCount: 0,
        totalCount: 0,
      };
    }
    const totalBuses = busCards.length;
    const arrivedBuses = busCards.filter(
      (bus) => bus.status === "arrived_hotel"
    ).length;
    if (arrivedBuses === 0) {
      return {
        status: "arriving_to_center",
        color: "brand",
        text: t("STATUS_ARRIVING_TO_CENTER"),
        icon: Search01Icon,
        arrivedCount: 0,
        totalCount: totalBuses,
      };
    } else if (arrivedBuses === totalBuses) {
      return {
        status: "complete_arrival",
        color: "success",
        text: t("STATUS_COMPLETE_ARRIVAL"),
        icon: Search01Icon,
        arrivedCount: arrivedBuses,
        totalCount: totalBuses,
      };
    } else {
      return {
        status: "partial_arrival",
        color: "success",
        text: t("STATUS_PARTIAL_ARRIVAL"),
        icon: Search01Icon,
        arrivedCount: arrivedBuses,
        totalCount: totalBuses,
      };
    }
  };

  const getStatusConfig = (arrival) => {
    return getArrivalStatus(arrival.bus_cards);
  };

  // Handle accommodate button click
  const handleAccommodateClick = (arrival) => {
    navigate(
      `/app/makkah/campaign-settlement/${arrival.type}/${arrival.id}/pilgrims`
    );
  };

  const createActions = (arrival) => {
    return (
      <button
        className="btn btn-primary flex-fill"
        onClick={() => handleAccommodateClick(arrival)}
        disabled={isFetching}
      >
        {t("ACCOMMODATE_PILGRIMS") || "تسكين الحجاج"}
      </button>
    );
  };

  const skeletonItems = Array.from({ length: 3 }, (_, i) => ({ id: i }));

  const handleDateChange = (e) => {
    setDateQuery(e.target.value);
  };
  const options = [
    { label: t("TODAY"), value: "today" },
    { label: t("ALL"), value: "all" },
  ];

  return (
    <Container fluid>
      <div className="d-flex gap-2 align-items-center mb-3">
        <div className="flex-fill">
          <AntInput
            prefix={<HugeiconsIcon icon={Search01Icon} />}
            type="text"
            placeholder={t("SEARCH_HERE")}
            value={searchValue}
            onChange={handleSearchChange}
            disabled={isFetching}
          />
        </div>
        <Flex gap={8}>
          <Badge count={Object.values(filters).filter(Boolean).length}>
            <BtnAsLink
              onClick={() => setIsFilterOpen(true)}
              className="btn-secondary"
              icon={<HugeiconsIcon icon={FilterIcon} />}
            >
              {t("FILTER")}
            </BtnAsLink>
          </Badge>
        </Flex>
      </div>
      <Flex justify="flex-start">
        {isNusuk && (
          <Radio.Group
            onChange={handleDateChange}
            value={dateQuery}
            options={options}
          />
        )}
      </Flex>
      <BsRow className="flex-wrap">
        {loading ? (
          skeletonItems.map((item) => <ArrivalCardSkeleton key={item.id} />)
        ) : arrivals.length === 0 ? (
          <div className="text-center py-5 w-100">
            <HugeiconsIcon size={40} icon={HajiIcon} />
            <h4 className="text-muted mb-2">
              {debouncedValue
                ? t("NO_SEARCH_RESULTS") || "لا توجد نتائج للبحث"
                : t("NO_ARRIVALS_FOUND") || "لا يوجد وصول"}
            </h4>
            <p className="text-muted mb-0">
              {debouncedValue
                ? t("NO_SEARCH_RESULTS_DESCRIPTION") ||
                "لم يتم العثور على وصولات تطابق البحث"
                : t("NO_ARRIVALS_DESCRIPTION") || "لم يتم العثور على أي وصول"}
            </p>
          </div>
        ) : (
          <ArrivalsList
            arrivals={arrivals}
            statusConfig={getStatusConfig}
            actions={isNusuk ? undefined : createActions}
            labelValues={labelValues}
            title={title}
            source={source}
            isNusuk={isNusuk}
          />
        )}
      </BsRow>

      <Divider className="mt-3" />

      {!!arrivalsRes?.data?.count && !loading && (
        <AntPagination
          className="sticky-bottom z-1 w-100 p-3 bg-white"
          hideOnSinglePage
          pageSize={10}
          current={currentPage}
          onChange={handlePageChange}
          total={arrivalsRes.data.count}
          showSizeChanger={false}
        />
      )}

      <GenericFilterModal
        isOpen={isFilterOpen}
        toggle={() => setIsFilterOpen(false)}
        onApply={handleFilterApply(() => {
          setCurrentPage(1);
        })}
        onReset={handleFilterReset(() => {
          setCurrentPage(1);
        })}
        filterFields={filterFields}
        filters={filters}
      />
    </Container>
  );
};

export default CampaignAccommodationList;

const ArrivalsList = (props) => {
  const {
    arrivals,
    statusConfig,
    actions,
    labelValues,
    title,
    source,
    isNusuk,
  } = props;

  return (
    <>
      {arrivals.map((arrival) => (
        <ArrivalCard
          key={arrival.id}
          title={isNusuk ? title[source] : title[arrival?.type]}
          titleValue={
            arrival?.report_number ??
            arrival?.request_number ??
            arrival?.serial ??
            "-"
          }
          {...{ arrival, labelValues }}
          statusConfig={statusConfig(arrival)}
          actions={actions && actions(arrival)}
        />
      ))}
    </>
  );
};
